import type { ActiveAISessionStage } from '@/stores/uiStore';
import type { AISessionSseEvent } from '@/types/ai-session';
import type { StageEditRunConfirmation } from '@/lib/ai-session/presentation/run-projection';
import {
  getStageEventBatchProgress,
  getStageEventBooleanField,
  getStageEventMessage,
  getStageEventStep,
  getStageEventStringField,
  readStageEventField,
} from '@/lib/ai-session/contracts/utils';
import type { AgentProgressEvent } from '@/lib/ai-session/presentation/agent-progress-types';
import { parseAgentProgressEvent } from './shared';
import type { StageClarification } from './shared';

export class AISessionStreamError extends Error {
  recoverable: boolean;
  reason?: string;
  errorCode?: string;
  code?: number;

  constructor(
    message: string,
    options?: {
      recoverable?: boolean;
      reason?: string;
      errorCode?: string;
      status?: number;
      cause?: unknown;
    }
  ) {
    super(message);
    this.name = 'AISessionStreamError';
    this.recoverable = options?.recoverable ?? false;
    this.reason = options?.reason;
    this.errorCode = options?.errorCode;
    this.code = options?.status;
    if (options?.cause !== undefined) this.cause = options.cause;
  }
}

export type StageStreamPatch = {
  latestSessionId?: string;
  isNewSession?: boolean;
  draftTextAppend?: string;
  streamStatusText?: string;
  clearStreamStatus?: boolean;
  activeStage?: ActiveAISessionStage;
  shouldShowPanel?: boolean;
  shouldDispatchSessionUpdate?: boolean;
  shouldSyncActiveRuns?: boolean;
  shouldDispatchProgress?: boolean;
  progressStep?: string;
  progressMessage?: string;
  progressBatchIndex?: number | null;
  progressBatchTotal?: number | null;
  progressEstimateSeconds?: number | null;
  progressRemainingSeconds?: number | null;
  completedSessionId?: string;
  sessionStageStatus?: string;
  latestAssetResult?: unknown;
  latestAssetChecklistDraft?: unknown;
  dispatchDetail?: Record<string, unknown>;
  requiresChecklistEdit?: boolean;
  editKind?: 'asset' | 'storyboard';
  editNeedsConfirmation?: boolean;
  editQuestion?: string;
  assetEditNeedsConfirmation?: boolean;
  assetQuestion?: string;
  acceptOrEdit?: {
    message: string;
    acceptLabel: string;
  };
  stageClarification?: StageClarification;
  /** 终态 interaction_required 即时物化的确认卡；activeRuns 仅对账。 */
  stageEditConfirmation?: StageEditRunConfirmation | null;
  agentProgress?: AgentProgressEvent;
  consultTextAppend?: string;
  consultTextReplace?: string;
  consultStreamActive?: boolean;
  consultRunId?: string;
  clearConsultText?: boolean;
  streamError?: string;
};

/** 取消/被替代只结束本轮展示，业务阶段由服务端重读决定。 */
export function buildInterruptedDonePatch(
  event: AISessionSseEvent,
  fallbackSessionId?: string | null
): StageStreamPatch | null {
  const status = getStageEventStringField(event, 'runStatus');
  if (event.event !== 'done' || (status !== 'cancelled' && status !== 'superseded')) return null;
  const sessionId = getStageEventStringField(event, 'sessionId') ?? fallbackSessionId;
  return {
    clearStreamStatus: true,
    clearConsultText: true,
    shouldSyncActiveRuns: true,
    ...(sessionId
      ? {
          latestSessionId: sessionId,
          shouldDispatchSessionUpdate: true,
          dispatchDetail: { sessionId },
        }
      : {}),
  };
}

export function buildBreakdownConsultTokenPatch(event: AISessionSseEvent): StageStreamPatch {
  const text = getStageEventStringField(event, 'text') ?? '';
  const replace = getStageEventBooleanField(event, 'replace') === true;
  const intent = getStageEventStringField(event, 'intent');
  // settlement 可能丢掉 intent；无 intent 仍按咨询正文增量展示。
  if (intent && intent !== 'consult') return {};
  const runId = getStageEventStringField(event, 'runId');
  const consultRunId = runId ? { consultRunId: runId } : {};
  if (replace) {
    return {
      consultTextReplace: text,
      consultStreamActive: Boolean(text),
      ...consultRunId,
    };
  }
  if (!text) return consultRunId;
  return {
    consultTextAppend: text,
    consultStreamActive: true,
    ...consultRunId,
  };
}

export function resolveAssetStreamSessionId(
  event: AISessionSseEvent,
  fallbackSessionId?: string | null
): string | undefined {
  if (typeof event.data.sessionId === 'string') {
    return event.data.sessionId;
  }
  return fallbackSessionId ?? undefined;
}

export function applyStageProgressFields(
  patch: StageStreamPatch,
  event: AISessionSseEvent,
  fallbackSessionId?: string | null,
  options?: { dispatchProgress?: boolean }
): void {
  const agentProgress = parseAgentProgressEvent(event);
  if (agentProgress) {
    patch.agentProgress = agentProgress;
  }
  const message = getStageEventMessage(event);
  const step = getStageEventStep(event) ?? (event.event === 'started' ? 'started' : null);
  const batchProgress = getStageEventBatchProgress(event);
  const batchErrors = readStageEventField(event, 'batchErrors');
  const degraded = readStageEventField(event, 'degraded');
  const estimateValue = readStageEventField(event, 'estimatedDuration');
  const estimateSeconds =
    typeof estimateValue === 'number' && Number.isFinite(estimateValue) && estimateValue > 0
      ? estimateValue
      : null;
  const remainingValue = readStageEventField(event, 'estimatedRemainingSeconds');
  const remainingSeconds =
    typeof remainingValue === 'number' && Number.isFinite(remainingValue) && remainingValue >= 0
      ? remainingValue
      : null;
  if (message) {
    patch.streamStatusText = message;
  }
  if (!message && !step) {
    return;
  }
  patch.progressMessage = message;
  if (step) {
    patch.progressStep = step;
  }
  if (batchProgress.batchIndex !== null) {
    patch.progressBatchIndex = batchProgress.batchIndex;
  }
  if (batchProgress.batchTotal !== null) {
    patch.progressBatchTotal = batchProgress.batchTotal;
  }
  if (estimateSeconds !== null) {
    patch.progressEstimateSeconds = estimateSeconds;
  }
  if (remainingSeconds !== null) {
    patch.progressRemainingSeconds = remainingSeconds;
  }
  const shouldDispatchProgress = options?.dispatchProgress ?? true;
  if (shouldDispatchProgress) {
    patch.shouldDispatchProgress = true;
  }
  const sessionId = resolveAssetStreamSessionId(event, fallbackSessionId ?? patch.latestSessionId);
  if (shouldDispatchProgress && sessionId) {
    patch.dispatchDetail = {
      ...patch.dispatchDetail,
      sessionId,
      stageStatus: 'running',
      ...(patch.activeStage?.assetRunMode ? { assetRunMode: patch.activeStage.assetRunMode } : {}),
      progressMessage: message,
      progressStep: step,
      ...(agentProgress?.title ? { progressTitle: agentProgress.title } : {}),
      ...(agentProgress?.summary ? { progressSummary: agentProgress.summary } : {}),
      ...(batchProgress.batchIndex !== null
        ? { progressBatchIndex: batchProgress.batchIndex }
        : {}),
      ...(batchProgress.batchTotal !== null
        ? { progressBatchTotal: batchProgress.batchTotal }
        : {}),
      ...(estimateSeconds !== null ? { progressEstimateSeconds: estimateSeconds } : {}),
      ...(remainingSeconds !== null ? { progressRemainingSeconds: remainingSeconds } : {}),
      ...(Array.isArray(batchErrors) ? { batchErrors } : {}),
      ...(typeof degraded === 'boolean' ? { degraded } : {}),
    };
  }
}
